import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { PHASE_LABEL_KEYS } from '@/consts/phases';
import regAside from '@/assets/images/reg-aside.jpg';

/**
 * Desktop-only left rail for the registration flow (Boris's DesktopFrame aside,
 * register.css .reg-dt-aside): a full-height lifestyle photo with the 4-phase
 * step list + quote anchored to the bottom over a bottom-weighted scrim. Purely
 * presentational; the "Step X of 4" text is the accessible signal.
 *
 * NOTE: `reg-aside.jpg` is Boris's prototype stock photo and is NOT licensed for
 * production. It is wired here so the rail matches the design; swap it for a
 * cleared/licensed photo (same crop, `center 30%/cover`) before shipping. The
 * bottom scrim keeps the cream step/quote text legible over the image.
 */
type Props = { currentPhase: number };

type StepState = 'done' | 'active' | 'upcoming';
const stateOf = (i: number, cur: number): StepState =>
  i < cur ? 'done' : i === cur ? 'active' : 'upcoming';

// Boris's rail (register.css .reg-dt-aside): the lifestyle photo fills the panel
// (`center 30%/cover`) with the step list + quote anchored to the BOTTOM. A
// bottom-weighted scrim (::before) — transparent over the top ~third so the
// image reads, dark toward the bottom — keeps the cream text legible (clears AA
// over the darkest stop). Step labels/quote use cream (#F4EFE7) at full/0.85/0.5
// alpha; chip numerals are ink on a light fill (active = lime, done = cream).
const Aside = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 28px;
  height: 100%;
  /* Fills the fixed-height frame column (Layout Shell/FlowCols); the rail is
     pinned and never scrolls, so it always spans exactly the viewport with the
     step nav + quote anchored at the bottom. */
  min-height: 0;
  padding: 56px 48px;
  border-radius: 0;
  overflow: hidden;
  color: ${BorisScreener.white};
  background: url(${regAside}) center 30% / cover no-repeat, ${BorisScreener.ink};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: linear-gradient(
      180deg,
      rgba(14, 11, 9, 0) 32%,
      rgba(14, 11, 9, 0.5) 62%,
      rgba(14, 11, 9, 0.92) 100%
    );
    /* Boris masks a blur to the lower region only: the photo reads sharp up top,
       softens behind the step list + quote. Mask fades in 34%→72%. */
    backdrop-filter: blur(7px);
    -webkit-backdrop-filter: blur(7px);
    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 34%, rgba(0, 0, 0, 1) 72%);
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 34%, rgba(0, 0, 0, 1) 72%);
  }
`;

// Boris `.reg-dt-aside-art`: a soft lime radial glow anchored off the bottom-right
// corner, clipped by the rail's overflow:hidden. Purely decorative.
const Art = styled.span`
  position: absolute;
  right: -70px;
  bottom: -60px;
  z-index: 0;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(
    circle at 35% 35%,
    rgba(247, 251, 194, 0.22),
    rgba(247, 251, 194, 0) 70%
  );
`;

const Steps = styled.ol`
  position: relative;
  z-index: 1;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Step = styled.li<{ $state: StepState }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 500;
  color: ${({ $state }) =>
    $state === 'upcoming'
      ? 'rgba(244,239,231,0.5)'
      : $state === 'done'
        ? 'rgba(244,239,231,0.85)'
        : BorisScreener.cream};
`;

const Num = styled.span<{ $state: StepState }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  flex-shrink: 0;
  /* Boris: active chip = lime, done chip = cream, upcoming = bordered/transparent. */
  border: 1.5px solid
    ${({ $state }) => ($state === 'upcoming' ? 'rgba(244,239,231,0.3)' : 'transparent')};
  background: ${({ $state }) =>
    $state === 'done'
      ? 'rgba(244,239,231,0.85)'
      : $state === 'active'
        ? BorisScreener.lime
        : 'transparent'};
  /* ink glyph on the light active/done chips; cream glyph on the dark upcoming chip. */
  color: ${({ $state }) => ($state === 'upcoming' ? BorisScreener.cream : BorisScreener.ink)};
`;

const Quote = styled.p`
  position: relative;
  z-index: 1;
  font-size: 26px;
  line-height: 1.3;
  font-weight: 500;
  margin: 0;
  max-width: 15ch;
  color: ${BorisScreener.cream};
  letter-spacing: -0.01em;
`;

const RegistrationPhaseRail: React.FC<Props> = ({ currentPhase }) => {
  const { t } = useTranslation();
  return (
    <Aside aria-label={t('reg-phase.aria-label')}>
      <Art aria-hidden="true" />
      <Steps>
        {PHASE_LABEL_KEYS.map((key, i) => {
          const s = stateOf(i, currentPhase);
          return (
            <Step key={key} $state={s} data-state={s} aria-current={s === 'active' ? 'step' : undefined}>
              <Num $state={s} aria-hidden="true">{s === 'done' ? '✓' : i + 1}</Num>
              {t(key)}
            </Step>
          );
        })}
      </Steps>
      <Quote>{t('reg-phase.quote')}</Quote>
    </Aside>
  );
};

export default RegistrationPhaseRail;
